import { PosthogEventType } from "@repo/app-types/client/client";
import type { PlaybookCriterionId } from "@repo/app-types/entities/playbook";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { CustomerInteractionDeepLink } from "@repo/app-types/helpers/customerInteractionUrl";
import { useCallback } from "react";
import { useAnalyticsContexts } from "../analytics/analyticsContext/useAnalyticsContexts.js";
import {
  useViewerAnalytics,
  type ViewerEventProperties,
} from "../teammate/viewerAnalytics.js";

export type CoachingGoalViewVariant =
  | "goal_card"
  | "note"
  | "legacy_grid"
  | "next_week";

/** The grading-page tab a reference opens, as resolved by its deep link. */
type CoachingReferenceTarget = CustomerInteractionDeepLink["tab"];

type CoachingManagerInputType = "note" | "next_goal";

interface CoachingAnalytics {
  captureGoalView: (args: { variant: CoachingGoalViewVariant }) => void;
  captureCallOpen: (args: { gradingId: GradingId }) => void;
  captureQuoteOpen: (args: {
    gradingId: GradingId;
    referenceTarget: CoachingReferenceTarget | null;
  }) => void;
  captureManagerInputSave: (args: {
    inputType: CoachingManagerInputType;
    isEdit: boolean;
    /** Newly saved goal criterion, before the provider receives server state. */
    criterionId?: PlaybookCriterionId;
  }) => void;
  captureManagerInputDelete: (args: {
    inputType: CoachingManagerInputType;
  }) => void;
}

type CoachingEventType =
  | typeof PosthogEventType.TeammateCoachingGoalView
  | typeof PosthogEventType.TeammateCoachingCallOpen
  | typeof PosthogEventType.TeammateCoachingQuoteOpen
  | typeof PosthogEventType.TeammateCoachingManagerInputSave
  | typeof PosthogEventType.TeammateCoachingManagerInputDelete;

/**
 * Captures coaching engagement events with their shared viewer and coaching
 * scopes. Both are resolved from required parent providers at emission time,
 * so event owners provide only event-specific properties.
 */
export function useCoachingAnalytics(): CoachingAnalytics {
  const { viewerId, captureWithViewer } = useViewerAnalytics();
  const {
    teammateId: coachedTeammateId,
    playbookId,
    periodId,
    criterionId,
  } = useAnalyticsContexts("coaching");

  const capture = useCallback(
    (
      event: CoachingEventType,
      eventProperties: ViewerEventProperties,
      criterionIdOverride?: PlaybookCriterionId,
    ): void => {
      captureWithViewer(event, {
        ...eventProperties,
        coached_teammate_id: coachedTeammateId,
        playbook_id: playbookId,
        period_id: periodId,
        criterion_id: criterionIdOverride ?? criterionId,
        is_self_view: viewerId === coachedTeammateId,
      });
    },
    [
      captureWithViewer,
      coachedTeammateId,
      criterionId,
      periodId,
      playbookId,
      viewerId,
    ],
  );

  const captureGoalView = useCallback<CoachingAnalytics["captureGoalView"]>(
    ({ variant }) =>
      capture(PosthogEventType.TeammateCoachingGoalView, {
        variant,
      }),
    [capture],
  );
  const captureCallOpen = useCallback<CoachingAnalytics["captureCallOpen"]>(
    ({ gradingId }) =>
      capture(PosthogEventType.TeammateCoachingCallOpen, {
        grading_id: gradingId,
      }),
    [capture],
  );
  const captureQuoteOpen = useCallback<CoachingAnalytics["captureQuoteOpen"]>(
    ({ gradingId, referenceTarget }) =>
      capture(PosthogEventType.TeammateCoachingQuoteOpen, {
        reference_target: referenceTarget,
        grading_id: gradingId,
      }),
    [capture],
  );
  const captureManagerInputSave = useCallback<
    CoachingAnalytics["captureManagerInputSave"]
  >(
    ({ inputType, isEdit, criterionId: savedCriterionId }) =>
      capture(
        PosthogEventType.TeammateCoachingManagerInputSave,
        {
          input_type: inputType,
          is_edit: isEdit,
        },
        savedCriterionId,
      ),
    [capture],
  );
  const captureManagerInputDelete = useCallback<
    CoachingAnalytics["captureManagerInputDelete"]
  >(
    ({ inputType }) =>
      capture(PosthogEventType.TeammateCoachingManagerInputDelete, {
        input_type: inputType,
      }),
    [capture],
  );

  return {
    captureGoalView,
    captureCallOpen,
    captureQuoteOpen,
    captureManagerInputSave,
    captureManagerInputDelete,
  };
}
